import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Switch, Text } from '@cowbell/ui/core';
import { faGlobe, faGrid2Plus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  isConnectionBroken,
  isProviderUsable,
  type ConnectableProvider,
} from '../../types/connected-apps';
import type { RenderProviderIconOptions } from './connected-apps-context';
import styles from './ConnectedAppsPopover.module.css';

export interface WebSearchToggle {
  readonly enabled: boolean;
  readonly onChange: (enabled: boolean) => void;
}

export interface ConnectedAppsPopoverProps {
  readonly providers: ConnectableProvider[];
  /** The "Web search" switch. Passed only when the tenant has
   *  enabled web search for this agent's audience; otherwise no row renders. */
  readonly webSearch?: WebSearchToggle;
  readonly renderIcon?: (options: RenderProviderIconOptions) => ReactNode;
  /** Host-supplied controls (e.g. the response-tone selector) shown above the apps. */
  readonly toneSlot?: ReactNode;
  readonly onManage?: () => void;
  readonly onConnect?: (provider: string) => void;
  readonly onSetEnabled?: (provider: string, enabled: boolean) => void;
}

function ProviderRow({
  provider,
  renderIcon,
  onSetEnabled,
  onConnect,
}: {
  readonly provider: ConnectableProvider;
  readonly renderIcon?: (options: RenderProviderIconOptions) => ReactNode;
  readonly onSetEnabled?: (provider: string, enabled: boolean) => void;
  readonly onConnect?: (provider: string) => void;
}) {
  const broken = isConnectionBroken(provider);
  // `enabled` is null when the backend does not report the field, which is
  // still the case — GET /user-connections returns no `enabled`, and there is
  // no column or endpoint behind it. Showing the switch then gives the user a
  // control that flips, calls an endpoint that cannot honour it, and reverts
  // on the next refetch. Hidden until the backend actually reports mute
  // support, at which point it appears with no further change here.
  const canToggle =
    provider.connected &&
    !broken &&
    provider.enabled !== null &&
    Boolean(onSetEnabled);

  return (
    <div className={styles.row}>
      <span className={styles.rowIcon}>
        {renderIcon?.({ provider, size: 20 })}
      </span>
      <Text className={styles.rowName}>{provider.displayName}</Text>

      {canToggle && (
        <Switch
          size="sm"
          color="green"
          checked={isProviderUsable(provider)}
          aria-label={`Use ${provider.displayName}`}
          onChange={(event) =>
            onSetEnabled?.(provider.provider, event.currentTarget.checked)
          }
        />
      )}
      {broken && (
        <button
          type="button"
          className={styles.rowAction}
          onClick={() => onConnect?.(provider.provider)}
        >
          Reconnect
        </button>
      )}
      {!provider.connected && (
        <button
          type="button"
          className={styles.rowAction}
          onClick={() => onConnect?.(provider.provider)}
        >
          Connect
        </button>
      )}
    </div>
  );
}

/**
 * Contents of the composer's connected-apps popover: the sources an agent may
 * draw on, and a per-app switch for the caller's own connections.
 *
 * Every route out of here goes to My Apps. The Integrations tab is tenant
 * configuration and root:root only, so a regular user sent there hits a
 * permission wall.
 */
export function ConnectedAppsPopover({
  providers,
  renderIcon,
  toneSlot,
  webSearch,
  onManage,
  onConnect,
  onSetEnabled,
}: ConnectedAppsPopoverProps) {
  // Two sections, as in the reference: what you have connected (with
  // switches), then what you could add. One flat list read as undifferentiated.
  const connected = providers.filter((p) => p.connected);
  const available = providers.filter((p) => !p.connected);
  // The heading counts what the switches say, the same count the pill shows:
  // a connected app switched off is not in use.
  const onCount = providers.filter(isProviderUsable).length;

  // The app list scrolls internally (`.list`, capped at eight rows) while
  // "Discover more apps" stays pinned below it — so with enough providers,
  // rows above the footer are hidden with no indication there's more to
  // scroll to. Tracks whether the list has unscrolled content below the
  // fold and shows a fade cue only then; it clears once the user scrolls
  // to the bottom.
  const listRef = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;
    const update = () => {
      setHasMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
    };
    update();
    el.addEventListener('scroll', update);
    return () => el.removeEventListener('scroll', update);
  }, [connected.length, available.length]);

  return (
    <div className={styles.panel}>
      {/* Per-turn web search. The agent may search the public web through the
          hosted provider; results come back as web citations, distinct from
          Cowbell data. Off means the tool is not offered at all this turn. */}
      {webSearch && (
        <div className={styles.row} data-testid="web-search-row">
          <span className={styles.rowIcon}>
            <FontAwesomeIcon icon={faGlobe} />
          </span>
          <Text className={styles.rowName}>Web search</Text>
          <Switch
            size="sm"
            color="green"
            checked={webSearch.enabled}
            aria-label="Web search"
            onChange={(event) =>
              webSearch.onChange(event.currentTarget.checked)
            }
          />
        </div>
      )}

      {toneSlot}

      <div className={styles.appsHeader}>
        <Text className={styles.appsCount}>
          {appsSummary(onCount, providers.length)}
        </Text>
        {onManage && available.length > 0 && (
          <button
            type="button"
            className={styles.headerAction}
            onClick={onManage}
          >
            Connect more
          </button>
        )}
      </div>

      <div className={styles.listWrap}>
        <div className={styles.list} ref={listRef}>
          {connected.map((provider) => (
            <ProviderRow
              key={provider.provider}
              provider={provider}
              renderIcon={renderIcon}
              onSetEnabled={onSetEnabled}
              onConnect={onConnect}
            />
          ))}

          {/* A separator only earns its place when it separates two groups.
              With nothing connected it would merely restate the count above
              it. */}
          {connected.length > 0 && available.length > 0 && (
            <Text className={styles.subHeader}>
              {available.length}{' '}
              {available.length === 1 ? 'app is' : 'apps are'} available to
              connect
            </Text>
          )}
          {available.map((provider) => (
            <ProviderRow
              key={provider.provider}
              provider={provider}
              renderIcon={renderIcon}
              onSetEnabled={onSetEnabled}
              onConnect={onConnect}
            />
          ))}
        </div>

        {hasMoreBelow && (
          <div className={styles.scrollFade} aria-hidden="true" />
        )}
      </div>

      {onManage && (
        <button
          type="button"
          className={styles.footerAction}
          onClick={onManage}
        >
          <FontAwesomeIcon icon={faGrid2Plus} />
          Discover more apps
        </button>
      )}
    </div>
  );
}

/** "Using all 7 apps", "Using 5 of 7 apps", "Not using any apps" — how many
 *  apps the agent can use this turn, short enough to stay on one line beside
 *  the header action. */
function appsSummary(on: number, total: number): string {
  const noun = total === 1 ? 'app' : 'apps';
  if (on === 0) {
    return 'Not using any apps';
  }
  if (total > 1 && on === total) {
    return `Using all ${total} ${noun}`;
  }
  return `Using ${on} of ${total} ${noun}`;
}
